import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonBase, IconButton, Paper, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  Close as CloseIcon, ArrowForward as NextIcon, ArrowBack as BackIcon, LightbulbOutlined as HintIcon,
  NorthEast as GoIcon, CheckCircle as DoneIcon, Insights as MonitorIcon, Science as AnalyzeIcon,
  AutoAwesome as AiIcon, Tune as ManageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from './TourContext';
import { TOUR_STEPS, visibleSteps, quickStartsFor, cardPosition } from './tourSteps';

const EASE = 'cubic-bezier(.2,.8,.2,1)';
const CARD_W = 384;
const Z = 2100;

const rise = keyframes`from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: none; }`;
const pop = keyframes`0% { opacity: 0; transform: scale(.6); } 70% { opacity: 1; transform: scale(1.04); } 100% { transform: scale(1); }`;
const pulse = keyframes`0% { opacity: .55; transform: scale(1); } 100% { opacity: 0; transform: scale(1.18); }`;
const draw = keyframes`from { stroke-dashoffset: 120; } to { stroke-dashoffset: 0; }`;
const fade = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const reduced = { '@media (prefers-reduced-motion: reduce)': { animation: 'none !important', transition: 'none !important' } };

const anchorEl = (name) => (name ? document.querySelector(`[data-tour="${name}"]`) : null);
const rectOf = (el, pad = 6) => {
  const r = el.getBoundingClientRect();
  return { left: r.left - pad, top: r.top - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
};

function Mark({ size = 56 }) {
  // The VerOps mark, its two halves arriving one after the other.
  return (
    <Box component="svg" viewBox="-20 -83 468 468" sx={{ width: size, height: size, overflow: 'visible' }} aria-hidden>
      <defs>
        <linearGradient id="tour-b" x1="0" y1="0" x2="0.35" y2="1"><stop offset="0" stopColor="#4a88cf" /><stop offset="1" stopColor="#1d3a63" /></linearGradient>
        <linearGradient id="tour-g" x1="1" y1="0" x2="0.7" y2="1"><stop offset="0" stopColor="#8fe00a" /><stop offset="1" stopColor="#4f8a17" /></linearGradient>
      </defs>
      <Box component="g" fill="url(#tour-b)" sx={{ transformOrigin: '30% 60%', animation: `${pop} 520ms ${EASE} both`, ...reduced }}>
        <polygon points="0,0 32,0 255,301 221,301" /><polygon points="65,0 97,0 181,115 150,115" /><polygon points="14,77 44,77 210,301 180,301" />
      </Box>
      <Box component="g" fill="url(#tour-g)" sx={{ transformOrigin: '70% 40%', animation: `${pop} 520ms ${EASE} 140ms both`, ...reduced }}>
        <polygon points="327,0 361,0 212,199 165,139 195,139 210,156" /><polygon points="394,0 428,0 236,257 220,235" /><polygon points="382,77 413,77 258,286 243,265" />
      </Box>
    </Box>
  );
}

const PILLARS = [
  { icon: <MonitorIcon fontSize="small" />, label: 'Monitor', text: 'Apps, servers, alerts' },
  { icon: <AnalyzeIcon fontSize="small" />, label: 'Analyze', text: 'Synthetics, explorer' },
  { icon: <AiIcon fontSize="small" />, label: 'AI', text: 'Ask Vera anything' },
  { icon: <ManageIcon fontSize="small" />, label: 'Manage', text: 'Team, keys, settings' },
];

function Progress({ index, total }) {
  return (
    <Stack direction="row" spacing={0.5} aria-hidden>
      {Array.from({ length: total }).map((_, i) => (
        <Box key={i} sx={{
          height: 4, borderRadius: 2, width: i === index ? 18 : 6,
          bgcolor: i <= index ? 'primary.main' : 'action.disabledBackground',
          transition: `width 300ms ${EASE}, background-color 300ms`, ...reduced,
        }} />
      ))}
    </Stack>
  );
}

/**
 * The first-login product tour. A spotlight glides from one sidebar area to the next with a
 * card that explains it and where it lives; intro and outro cards bracket the walk. Keyboard:
 * → / Enter next, ← back, Esc closes. Closing at any point counts as seen.
 */
export default function ProductTour() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { open, finish } = useTour();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [view, setView] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [cardH, setCardH] = useState(240);
  const [cardEl, setCardEl] = useState(null);

  // Stops are decided when the tour opens, from what is actually on screen.
  const steps = useMemo(() => (open ? visibleSteps(TOUR_STEPS, (a) => Boolean(anchorEl(a))) : []),
    [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setI(0); }, [open]);
  const step = steps[i];
  const anchored = step && !step.kind;

  const measure = useCallback(() => {
    setView({ w: window.innerWidth, h: window.innerHeight });
    const el = anchored ? anchorEl(step.anchor) : null;
    if (el) el.scrollIntoView({ block: 'nearest' });
    setRect(el ? rectOf(el, step.anchor === 'sidebar' ? 0 : 6) : null);
  }, [anchored, step]);

  // Measure now and again once the sidebar has finished expanding its groups.
  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    const t = setTimeout(measure, 260);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [open, measure]);
  useLayoutEffect(() => { if (cardEl) setCardH(cardEl.offsetHeight); }, [cardEl, i, rect]);

  const next = useCallback(() => (i < steps.length - 1 ? setI(i + 1) : finish()), [i, steps.length, finish]);
  const back = useCallback(() => setI((x) => Math.max(0, x - 1)), []);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, next, back, finish]);

  if (!open || !step) return null;

  const scrim = alpha(theme.palette.mode === 'dark' ? '#000' : '#0b1220', 0.62);
  const first = (user?.firstName || user?.username || '').split(/[\s@._]/)[0];
  const pos = rect ? cardPosition(rect, { w: CARD_W, h: cardH }, view, step.placement) : null;
  const n = steps.filter((s) => !s.kind).length;
  const k = steps.slice(0, i + 1).filter((s) => !s.kind).length - 1;
  const go = (path) => { if (path.startsWith('http')) window.open(path, '_blank', 'noopener'); else navigate(path); };

  return (
    <Box role="dialog" aria-modal="true" aria-label={`Product tour: ${step.title}`} sx={{ position: 'fixed', inset: 0, zIndex: Z }}>
      {/* Scrim: a spotlight hole around the anchor, or a full veil for the intro and outro. */}
      {rect ? (
        <>
          <Box sx={{
            position: 'fixed', left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderRadius: '12px',
            boxShadow: `0 0 0 9999px ${scrim}`, pointerEvents: 'none',
            transition: `left 450ms ${EASE}, top 450ms ${EASE}, width 450ms ${EASE}, height 450ms ${EASE}`, ...reduced,
          }} />
          <Box key={step.id} sx={{
            position: 'fixed', left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderRadius: '12px',
            border: `2px solid ${theme.palette.primary.main}`, pointerEvents: 'none',
            animation: `${pulse} 1.6s ease-out 450ms infinite`, ...reduced,
          }} />
        </>
      ) : (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: scrim, backdropFilter: 'blur(3px)', animation: `${fade} 300ms both`, ...reduced }} />
      )}

      {/* Anchored card */}
      {anchored && pos && (
        <Paper key={step.id} ref={setCardEl} elevation={12} sx={{
          position: 'fixed', left: pos.left, top: pos.top, width: CARD_W, p: 2.5, borderRadius: 3,
          animation: `${rise} 380ms ${EASE} both`, ...reduced,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.12em', lineHeight: 1.5 }}>
              {step.eyebrow}
            </Typography>
            <Tooltip title="Close the tour (Esc)">
              <IconButton size="small" onClick={finish} aria-label="Close the tour"><CloseIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mb: 1 }}>{step.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{step.body}</Typography>
          {step.hint && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.07) }}>
              <HintIcon sx={{ fontSize: 18, color: 'primary.main', mt: '1px' }} aria-hidden />
              <Typography variant="caption" sx={{ lineHeight: 1.5 }}>{step.hint}</Typography>
            </Stack>
          )}
          {step.path && (
            <Button size="small" endIcon={<GoIcon fontSize="small" />} onClick={() => go(step.path)}
                    sx={{ mt: 1.25, px: 0, textTransform: 'none', fontWeight: 600 }}>
              Open {step.title.split(/[:(]/)[0].trim()} now
            </Button>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2.25 }}>
            <Stack spacing={0.75}>
              <Progress index={k} total={n} />
              <Typography variant="caption" color="text.secondary">{k + 1} of {n}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" onClick={back} aria-label="Previous"><BackIcon fontSize="small" /></IconButton>
              <Button size="small" variant="contained" endIcon={<NextIcon fontSize="small" />} onClick={next} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {i === steps.length - 2 ? 'Finish' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* Intro */}
      {step.kind === 'intro' && (
        <Box sx={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', p: 2 }}>
          <Paper elevation={16} sx={{ width: 'min(560px, 100%)', p: { xs: 3, sm: 4.5 }, borderRadius: 4, textAlign: 'center', animation: `${rise} 480ms ${EASE} both`, ...reduced }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><Mark size={64} /></Box>
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
              {first ? `Welcome, ${first}` : step.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3, lineHeight: 1.6 }}>{step.body}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.25, mb: 3.5 }}>
              {PILLARS.map((p, j) => (
                <Box key={p.label} sx={{
                  p: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', textAlign: 'left',
                  animation: `${rise} 420ms ${EASE} ${180 + j * 90}ms both`, ...reduced,
                }}>
                  <Box sx={{ color: 'primary.main', mb: 0.5, display: 'flex' }}>{p.icon}</Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, display: 'block' }}>{p.text}</Typography>
                </Box>
              ))}
            </Box>
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button onClick={finish} sx={{ textTransform: 'none' }}>Skip for now</Button>
              <Button variant="contained" size="large" endIcon={<NextIcon />} onClick={next} autoFocus sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
                Show me around
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Outro */}
      {step.kind === 'outro' && (
        <Box sx={{ position: 'fixed', inset: 0, display: 'grid', placeItems: 'center', p: 2 }}>
          <Paper elevation={16} sx={{ width: 'min(620px, 100%)', p: { xs: 3, sm: 4.5 }, borderRadius: 4, animation: `${rise} 480ms ${EASE} both`, ...reduced }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Box component="svg" viewBox="0 0 48 48" sx={{ width: 44, height: 44 }} aria-hidden>
                <circle cx="24" cy="24" r="22" fill={alpha(theme.palette.success.main, 0.12)} />
                <Box component="path" d="M14 25 l7 7 l13 -15" fill="none" stroke={theme.palette.success.main} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                     sx={{ strokeDasharray: 120, animation: `${draw} 600ms ${EASE} 150ms both`, ...reduced }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{step.title}</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mb: 2.5 }}>{step.body}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {quickStartsFor(isAdmin).map((q, j) => (
                <ButtonBase key={q.id} onClick={() => { finish(); go(q.href || q.path); }} sx={{
                  display: 'block', textAlign: 'left', p: 1.75, borderRadius: 2.5, border: 1, borderColor: 'divider',
                  transition: `transform 160ms ${EASE}, border-color 160ms, box-shadow 160ms`,
                  animation: `${rise} 420ms ${EASE} ${120 + j * 80}ms both`,
                  '&:hover, &:focus-visible': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 4 }, ...reduced,
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{q.title}</Typography>
                    <GoIcon sx={{ fontSize: 16, color: 'primary.main' }} aria-hidden />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{q.body}</Typography>
                </ButtonBase>
              ))}
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">
                <DoneIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5, color: 'success.main' }} />
                Replay this any time from your profile menu → Product tour
              </Typography>
              <Button variant="contained" onClick={finish} sx={{ textTransform: 'none', fontWeight: 700 }}>Start exploring</Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
